import type { router as Router } from '../routes';
import { useCards } from '../store/cards';
import { sessionHooks, useSession } from '../store/session';
import { useSettings } from '../store/settings';
import { useTimer } from '../store/timer';
import { scheduleFlush, syncHooks, useSync } from '../sync/engine';
import { setLiveActive } from '../sync/live';
import { useInstall } from '../update/install';
import { checkForUpdates } from '../update/watch';
import { installBackButton } from './backButton';
import { installLaunchActions } from './launch';
import { clearNotifier, configureNotifier, ensureAlarmChannel, holdNotifier, installNotificationTaps, reconcileNotifications } from './notifications';
import { isNative } from './platform';
import { installShareTarget } from './shareTarget';
import { installStatusBar } from './statusBar';

let installed = false;

/** Wires the Android shell to the app once; a no-op on the web. */
export async function initNative(router: typeof Router): Promise<void> {
  if (!isNative || installed) return;
  installed = true;
  const { App } = await import('@capacitor/app');
  installBackButton(App, router);
  await installStatusBar();
  await installShareTarget(router);
  await installLaunchActions(router);
  await installNotificationTaps(router);
  await ensureAlarmChannel();
  await App.addListener('appStateChange', ({ isActive }) => {
    setLiveActive(isActive);
    if (isActive) {
      useTimer.getState().ensure();
      scheduleFlush(0);
      void reconcileNotifications();
      checkForUpdates();
      useInstall.getState().resume();
    }
  });

  syncHooks.afterPull.push(reconcileNotifications);
  // Signed out: nothing of the account stays in the shade, and the background job stops asking.
  sessionHooks.beforeSignOut.push(clearNotifier);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const later = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void reconcileNotifications(), 1000);
  };
  useCards.subscribe((s, prev) => {
    if (s.cards !== prev.cards) later();
  });
  useSettings.subscribe((s, prev) => {
    if (s.locale !== prev.locale || s.todayShade !== prev.todayShade) void configureNotifier();
    if (
      s.checkinTime !== prev.checkinTime ||
      s.locale !== prev.locale ||
      s.timerRing !== prev.timerRing ||
      s.todayShade !== prev.todayShade ||
      s.columnCount !== prev.columnCount ||
      s.columnLabels !== prev.columnLabels ||
      s.bandLabels !== prev.bandLabels ||
      s.bandPreset !== prev.bandPreset
    )
      later();
  });
  useSession.subscribe((s, prev) => {
    if (s.token !== prev.token || s.status !== prev.status) void configureNotifier();
    if (s.boards !== prev.boards || s.defaults !== prev.defaults) later();
  });
  useTimer.subscribe((s, prev) => {
    if (s.running !== prev.running) void reconcileNotifications();
  });
  useSync.subscribe((s, prev) => {
    if (s.pending > 0 !== prev.pending > 0) void holdNotifier(s.pending > 0);
  });
  void holdNotifier(useSync.getState().pending > 0);
  void configureNotifier();
  void reconcileNotifications();
}
